import {
  Center,
  Cylinder,
  Html,
  Plane,
  Resize,
  RoundedBox,
  Svg,
  Text,
} from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { Interactive, useController, useXR } from "@react-three/xr";
import React, { useMemo } from "react";
import * as THREE from "three";
import { Scene, TourData } from "../../../../Types";
import {
  UseAssetsQuery,
  UseDownloadAsset,
} from "../../../../api/calls/Queries";
import {
  useCurrentSceneStore,
  useDataRootStore,
  useParametersStore,
} from "../../../../api/Zustand";
import { useVolumeStore } from "../../../../state management/zustand/common-stores";
import XRMap from "./Map";
import { useCursorShrinkingStore } from "./CursorNew";
import { useXRPopupStore } from "../Popups/XRHotspots";
import { useViewerMapStore } from "../../../../state management/zustand/viewer-stores";

const dir = new THREE.Vector3();
const pos = new THREE.Vector3();

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

function Exit(props: { TourData: TourData }) {
  const session = useXR((xr) => {
    return xr.session;
  });

  const { tourID } = useParametersStore();

  const { currentSceneID, setCurrentSceneID } = useCurrentSceneStore();

  const viewerMap = useViewerMapStore();

  const posRef = React.useRef<THREE.Group | null>(null);

  const cyl = React.useRef(null);

  const { volume: vol } = useVolumeStore();

  const currentScene: Scene | undefined = React.useMemo(() => {
    const temp = props.TourData.scenes.find((one) => one.id === currentSceneID);

    return temp;
  }, [props.TourData, currentSceneID]);

  React.useEffect(() => {
    if (currentScene?.mapAutoOpen) viewerMap.setOpen(currentScene.map!);
  }, [currentScene, viewerMap]);

  const { root } = useDataRootStore();

  useFrame((state) => {
    posRef.current?.lookAt(state.camera.position);
    state.camera.getWorldDirection(dir);
    state.camera.getWorldPosition(pos);

    if (dir.y <= -Math.cos(Math.PI / 3) || viewerMap.isOpen) {
      if (cyl.current && posRef.current) {
        state.raycaster.set(pos, base.lerp(dir, 0.5));

        const intersect = state.raycaster.intersectObject(cyl.current);
        if (intersect[0]) {
          const pointing = intersect[0].faceIndex;

          if (pointing) {
            if (viewerMap.isOpen) {
              posRef.current.position.lerp(positions2[pointing / 2], 0.1);
            } else posRef.current.position.lerp(positions[pointing / 2], 0.1);
          } else posRef.current.position.lerp(positions[0], 0.1);
        }
      }
    } else {
      posRef.current?.position.lerp(new THREE.Vector3(0, -40, 0), 0.1);
    }
  });

  const volumeShouldBeDisplayed: boolean = React.useMemo(() => {
    const hasAudioHS = !!currentScene?.hotspots.find(
      (one) => one.type === "audio"
    );

    return hasAudioHS || !!currentScene?.audio;
  }, [currentScene]);

  const xrPop = useXRPopupStore();

  if (!!session)
    return (
      <group>
        <Cylinder
          visible={false}
          rotation={[0, (67.5 * Math.PI) / 180, 0]}
          ref={cyl}
          args={[h * 5, 0, r * 5, 8, 1, true]}
          material={
            new THREE.MeshBasicMaterial({
              wireframe: true,
              side: THREE.DoubleSide,
            })
          }
        />

        <group position={[0, -40, 0]} ref={posRef} scale={10}>
          {/* VOLUME */}

          <group
            visible={volumeShouldBeDisplayed}
            position={[0.75, -0.35, 0.01]}
            scale={0.35}
          >
            <Text fontWeight={800} position={[0, 0.35, 0]} fontSize={0.3}>
              Volume
            </Text>

            <Text fontSize={0.5} fontWeight={900} position={[0, -0.05, 0]}>
              {Math.floor(vol * 100)}
            </Text>

            <VolumeButton increase={true} pos={[0.9, 0, 0]} scale={0.75} />

            <VolumeButton increase={false} pos={[-0.85, 0, 0]} scale={0.75} />
          </group>

          <Button
            fn={() => {
              if (xrPop.isOpen) {
                return;
              } else setCurrentSceneID(props.TourData.startingScene!);
            }}
            pos={[-0.95, 0.35, 0]}
            btnIndex={1}
          >
            <Resize scale={0.4} position={[0, 0.025, 0]}>
              <Center position={[0, 0, 0.1]}>
                <mesh renderOrder={4}>
                  <Svg
                    renderOrder={3}
                    fillMaterial={crossMaterial}
                    src={root + "/svg/home.svg"}
                  ></Svg>
                </mesh>
              </Center>
            </Resize>
          </Button>

          <Button fn={() => session.end()} pos={[0.95, 0.35, 0]} btnIndex={2}>
            <group position={[-0.0, 0, 0]} rotation={[0, 0, Math.PI / 4]}>
              <Plane
                renderOrder={3}
                material={crossMaterial}
                args={[0.4, 0.04]}
              ></Plane>

              <Plane
                rotation={[0, 0, Math.PI / 2]}
                renderOrder={3}
                material={crossMaterial}
                args={[0.4, 0.04]}
              ></Plane>
            </group>
          </Button>

          {currentScene?.map && (
            <Button
              pos={[-0.95, -0.35, 0.1]}
              fn={() => viewerMap.setOpen(currentScene.map!)}
              btnIndex={4}
              active={viewerMap.isOpen}
            >
              <Center scale={0.0006} position={[0, 0, 0.03]}>
                <Svg src={root + "/svg/map.svg"}></Svg>
              </Center>
            </Button>
          )}

          {currentScene && viewerMap.isOpen && (
            <XRMap scene={currentScene} tourData={props.TourData} />
          )}

          <AudioWrapper
            vol={vol}
            currentScene={currentScene}
            tourID={tourID}
          ></AudioWrapper>

          {/* BACKGROUND */}
          <RoundedBox
            renderOrder={2}
            args={[2.5, 1.2, 0.22]}
            position={[0, 0, -0.3]}
            radius={0.1}
            bevelSegments={0}
            smoothness={3}
          >
            <meshBasicMaterial
              // transparent
              // depthTest={false}
              opacity={0.5}
              color={"gray"}
            ></meshBasicMaterial>
          </RoundedBox>
        </group>
      </group>
    );
}

export default Exit;

const AudioWrapper = (props: {
  tourID: number;
  currentScene: Scene | undefined;
  vol: number;
}) => {
  const { root } = useDataRootStore();
  const { data: audioList } = UseAssetsQuery(props.tourID, 3);

  const currentAudio: string | undefined = useMemo(() => {
    const temp = audioList?.find(
      (one) => one.assetId === props.currentScene?.audio
    );

    return temp?.assetKeyFull;
  }, [audioList, props.currentScene]);

  const { data } = UseDownloadAsset(currentAudio);

  const audioRef = React.useRef<HTMLAudioElement>(null);

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = props.vol;
    }
  }, [props.vol]);

  const [paused, setPaused] = React.useState<boolean>(true);

  return (
    data && (
      <>
        <Button
          fn={() => {
            if (audioRef.current) {
              if (audioRef.current.paused) audioRef.current.play();
              else audioRef.current.pause();
            }
          }}
          btnIndex={4}
          pos={[0, -0.35, 0.1]}
          active={paused === false}
        >
          <Center
            renderOrder={5}
            position={[-0.0, 0.0, 0.01]}
            // position={[-0.05, 0.035, 0.2]}
            scale={0.0006}
          >
            {audioRef.current?.paused ? (
              <Svg
                fillMaterial={{ color: "white" }}
                src={root + "/svg/headphones.svg"}
              ></Svg>
            ) : (
              <Svg
                fillMaterial={{ color: "white" }}
                src={root + "/svg/pause.svg"}
              ></Svg>
            )}
          </Center>
        </Button>
        <Html>
          <audio
            src={data}
            ref={audioRef}
            onLoadedData={(e) => {
              return e;
            }}
            onEnded={(e) =>
              setPaused(
                // @ts-ignore
                e.target?.paused ?? true
              )
            }
            onPause={(e) => {
              setPaused(true);
              return e;
            }}
            onPlay={(e) => {
              setPaused(false);
              return e;
            }}
          />
        </Html>
      </>
    )
  );
};

const Button = (props: {
  children: any;
  pos: [number, number, number];
  fn: () => void;
  btnIndex: number;
  active?: boolean;
}) => {
  const cursor = useCursorShrinkingStore();

  const { children, pos, fn, btnIndex, active } = props;

  const [hover, setHover] = React.useState<number>();
  const matRef = React.useRef<THREE.MeshBasicMaterial>(null);
  const circleRef = React.useRef<THREE.Mesh>(null);

  const gaze = useController("none");

  useFrame(() => {
    if (matRef.current) {
      if (hover === btnIndex) {
        circleRef.current?.scale.lerp(new THREE.Vector3(1.3, 1.3, 1.3), 0.1);
      } else {
        circleRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    }
  });

  return (
    <Interactive
      onHover={() => {
        setHover(btnIndex);
        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);
        if (gaze) {
          timeout = setTimeout(() => {
            cursor.setShrinking(true);
            timeout2 = setTimeout(() => {
              fn();
              cursor.setShrinking(false);
            }, 2e3);
          }, 1e3);
        }
      }}
      onBlur={() => {
        setHover(-1);

        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);
      }}
      onSelect={() => {
        fn();
        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);
      }}
    >
      <Center position={pos} scale={0.5}>
        <group position={[0, 0, 0.025]}>{children}</group>

        <mesh
          renderOrder={3}
          position={[0, 0, 0.01]}
          name="hotspot"
          ref={circleRef}
        >
          <circleGeometry args={[0.35, 32]} />
          <meshBasicMaterial
            opacity={0.5}
            // transparent
            // depthTest={false}
            color={active ? "#60a5fa" : "#aaa"}
            ref={matRef}
          />
        </mesh>
      </Center>
    </Interactive>
  );
};

const VolumeButton = (props: {
  increase: boolean;
  pos: THREE.Vector3 | [number, number, number];
  scale: number;
}) => {
  const [timerRunning, setTimerRunning] = React.useState<boolean>(false);
  const [hover, setHover] = React.useState<boolean>(false);

  const { increase } = props;

  const gaze = useController("none");

  const { volume, setVolumeXR } = useVolumeStore();

  const intervalRef = React.useRef<NodeJS.Timeout>();
  React.useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (timerRunning) {
        if (increase) {
          setVolumeXR(volume + 0.05);
        } else setVolumeXR(volume - 0.05);
      }
    }, 5e2);
    return () => clearInterval(intervalRef.current);
  }, [timerRunning, volume, setVolumeXR, increase]);

  const matRef = React.useRef<THREE.MeshBasicMaterial>(null);
  const matRef2 = React.useRef<THREE.MeshBasicMaterial>(null);
  const matRefBG = React.useRef<THREE.MeshBasicMaterial>(null);

  const circleRef = React.useRef<THREE.Mesh>(null);
  const button = React.useRef<THREE.Group>(null);
  useFrame(() => {
    if (hover) {
      circleRef.current?.scale.lerp(new THREE.Vector3(1.3, 1.3, 1.3), 0.1);
    } else {
      circleRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.25);
    }

    if (timerRunning) {
      button.current?.position.lerp(new THREE.Vector3(0, 0, 0.2), 0.1);
    } else button.current?.position.lerp(new THREE.Vector3(0, 0, 0), 0.1);
  });

  const cursor = useCursorShrinkingStore();

  return (
    <Interactive
      onHover={() => {
        setHover(true);
        clearTimeout(timeout);

        if (gaze) {
          timeout = setTimeout(() => {
            setTimerRunning(true);
            cursor.setShrinking(true);
          }, 1e3);
        }
      }}
      onSelectStart={() => setTimerRunning(true)}
      onSelectEnd={() => setTimerRunning(false)}
      onBlur={() => {
        setHover(false);
        cursor.setShrinking(false);
        clearTimeout(timeout);
        setTimerRunning(false);
      }}
    >
      <group position={props.pos} scale={props.scale}>
        <group ref={button}>
          <mesh position={[0, 0, 0.1]}>
            <planeGeometry args={[0.5, 0.1]} />

            <meshBasicMaterial ref={matRef} color={"#fff"} />
          </mesh>

          {props.increase && (
            <mesh position={[0, 0, 0.1]}>
              <planeGeometry args={[0.1, 0.5]} />
              <meshBasicMaterial ref={matRef2} color={"#fff"} />
            </mesh>
          )}

          <mesh renderOrder={3} name="hotspot" ref={circleRef}>
            <circleGeometry args={[0.35, 32]} />
            <meshBasicMaterial color={"#aaa"} ref={matRefBG} />
          </mesh>
        </group>
      </group>
    </Interactive>
  );
};

const crossMaterial = new THREE.MeshBasicMaterial({
  color: "white",

  depthTest: false,
  transparent: true,
});

const base = new THREE.Vector3(0, 0, 0);

const r = 4;
const h = 4;

const angledPos = r * Math.sin((45 * Math.PI) / 180);

const N = new THREE.Vector3(r, -h, 0);
const S = new THREE.Vector3(-r, -h, 0);
const W = new THREE.Vector3(0, -h, -r);
const E = new THREE.Vector3(0, -h, r);

const NE = new THREE.Vector3(angledPos, -h, angledPos);
const SE = new THREE.Vector3(-angledPos, -h, angledPos);
const NW = new THREE.Vector3(angledPos, -h, -angledPos);
const SW = new THREE.Vector3(-angledPos, -h, -angledPos);

const positions = [
  N.multiplyScalar(10),
  NW.multiplyScalar(10),
  W.multiplyScalar(10),
  SW.multiplyScalar(10),
  S.multiplyScalar(10),
  SE.multiplyScalar(10),
  E.multiplyScalar(10),
  NE.multiplyScalar(10),
];

const positions2 = [
  positions[0],
  positions[0],
  positions[2],
  positions[2],
  positions[4],
  positions[4],
  positions[6],
  positions[6],
];

const test = (
  <svg
    id="color-fill"
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    width="100%"
    height="300"
  >
    <polygon
      className="hex"
      points="300,150 225,280 75,280 0,150 75,20 225,20"
      fill="#ff0000"
    ></polygon>
  </svg>
);
